import React, {FC} from 'react'
import cls from "../forms.module.scss"
import {useDispatch, useSelector} from "react-redux";
import {IState} from "../../../../types/state";
import shopApi from "../../../../api/shop";
import {CheckToastSvg, FailSvg} from "../../../../svg";
import {useToast} from "../../../../atoms/Toast/useToast";
import {useRouter} from "next/router";
import {addAndResetForm, setFormErrors, setFormPopupOpen} from "../../../../store/formbuilder";
import {useReCaptcha} from "next-recaptcha-v3";
import {FormSubmission} from "../submission";

interface IProps {
    formId: number,
    formCode: string,
    index: number,
    element: {
        className: string,
        label: string,
        name: string,
        style: string,
        subtype: "button" | "submit" | "reset",
        value: string,
    },
    formData: Array<{
        type: string,
        value: string | number | string[],
        multiple: boolean,
        values: Array<{
            label: string,
            value: string,
            selected: boolean,
        }>,
    }>;
    recaptchaActive: string,
    submission: FormSubmission,
    isSubmitting: boolean,
    setIsSubmitting: (value: boolean) => void
}

const Button: FC<IProps> = ({formId, formCode, index, element, formData, recaptchaActive, submission, isSubmitting, setIsSubmitting}) => {
    const {
        className = "",
        label = "",
        name = "",
        style = "",
        subtype = "button",
        value = "",
    } = element

    const data = useSelector((state: IState) => (state.formBuilder.forms.find(el => el.id === formId) || {formData: []}).formData)
    const cartToken = useSelector((state: IState) => state.cartToken.cartToken);
    const dispatch = useDispatch();
    const toast = useToast();
    const router = useRouter();
    const {executeRecaptcha} = useReCaptcha();

    const showSubmissionError = (message = "Unable to submit the form. Please try again.") => {
        toast.open(
            <span className="d-flex chek-fms">
                <FailSvg/>
                {message}
            </span>
        );
    };

    const handleSubmit = async () => {
        const handleElementValue = (element: {
            type: string,
            label: string,
            value: string,
            name: string,
            values: Array<{
                label: string,
                value: string,
                selected: boolean,
            }>,
            multiple: boolean
        }): Record<string, Record<string, string | number | Array<string>>> => {
            const {
                label = "",
                value = "",
                name = "",
                values = [],
                multiple = false
            } = element

            return {
                "header": {
                    // heading: label
                },
                "text": {
                    [name]: value
                },
                "number": {
                    [name]: value
                },
                "date": {
                    [name]: value
                },
                "file": {
                    [name]: value
                },
                "button": {},
                "checkbox-group": {
                    [name]: values
                        .filter(el => el.selected)
                        .map(el => el.value)
                },
                "radio-group": {
                    [name]: (values
                        .find(el => el.selected) || {value: ""}).value
                },
                "textarea": {
                    [name]: value
                },
                "select": {
                    [name]: values
                        .filter(el => el.selected)
                        .map(el => el.value)
                },
            }
        }
        let newData: Record<string, string | number | Array<string>> = {}
        let formData = new FormData();
        for (const el of data) {
            newData = {
                ...newData,
                ...handleElementValue(el)[el.type],
                ...(el.type === "hidden" && el.forSku ? {
                    [el.name]: el.value
                } : {}),
                ...(el.type === "hidden" && el.forPageUrl ? {
                    [el.name]: el.value
                } : {})
            }
        }

        for (let key in newData) {
            if (Array.isArray(newData[key])) {
                (newData[key] as Array<string>).map((el: string, index: number) => {
                    formData.append(key + '[' + index + ']', el);
                })
            } else {
                formData.append(key, newData[key] as string | Blob);
            }
        }

        // Paid forms need the storefront cart API token so the backend can attach
        // the product cart created by the submission to the same cart the checkout reads.
        if (Object.prototype.hasOwnProperty.call(newData, "paying_item_id")) {
            if (!cartToken) {
                showSubmissionError("Checkout is not ready yet. Please try again.");
                return;
            }
            formData.set("api_token", cartToken);
        }

        let token: string | null;
        try {
            token = submission.begin(formData);
        } catch {
            showSubmissionError();
            return;
        }
        if (!token) return;

        setIsSubmitting(true);
        try {
            formData.set("submission_token", token);
            if (+recaptchaActive) {
                const recaptchaToken = await executeRecaptcha("form_submit");
                if (!recaptchaToken) {
                    throw new Error("reCAPTCHA did not return a token");
                }
                formData.set("re_token", recaptchaToken);
            }

            const res = await shopApi.submitFormBuilder(formData, formCode);
            if (res?.errors) {
                dispatch(setFormErrors(res.errors));
                showSubmissionError(res.message);
                return;
            }
            // The current backend returns CAPTCHA failures in an array.
            if (Array.isArray(res) && res[0]?.status === "error") {
                showSubmissionError(res[0].message);
                return;
            }
            if (!res || (res.type !== "show_message" && res.type !== "redirect")) {
                throw new Error("Unexpected form submission response");
            }

            dispatch(setFormErrors({}));
            dispatch(setFormPopupOpen({id: null, open: false}));
            if (res.type === "show_message") {
                toast.open(
                    <span className="d-flex chek-fms">
                        <CheckToastSvg/>
                        {res.data}
                    </span>
                );
            } else {
                await router.push(res.data);
            }
        } catch {
            showSubmissionError();
        } finally {
            submission.finish();
            setIsSubmitting(false);
        }
    }


    const handleReset = () => {
        if (!submission.reset()) return;
        dispatch(setFormErrors({}));
        dispatch(addAndResetForm({
            id: formId,
            formData
        }))
    }

    const handleOnClickByType = {
        "submit": handleSubmit,
        "reset": handleReset,
        "button": () => {
        }
    }

    return <button
        className={`${cls.form_button} ${className}`}
        type="button"
        disabled={isSubmitting && (subtype === "submit" || subtype === "reset")}
        aria-busy={subtype === "submit" ? isSubmitting : undefined}
        onClick={handleOnClickByType[subtype]}
    >
        {label}
    </button>
}
export default Button
